import { FC, useMemo } from 'react';

import { getTotalBalance } from 'common/bankAccount/utils';
import { useCompanyBankAccounts } from 'common/hooks/useCompanyBankAccounts';

import { useBankOverviewSearchParams } from '../hooks/useBankOverviewSearchParams';
import { useTransactionsFilters } from '../hooks/useTransactionsFilters';
import AccountHeaderWithBalance from './AccountHeaderWithBalance';
import EmployeeTransactionsHeader from './EmployeeTransactionsHeader';

const DEFAULT_CURRENCY = 'EUR';

const PLACEHOLDER_BANK_ACCOUNT = {
  currency: DEFAULT_CURRENCY,
  currentBalance: 123456,
  nextBalance: 123456,
};

const AccountHeader: FC = () => {
  const { bankAccounts, error, loading } = useCompanyBankAccounts();
  const { bankAccountId: selectedBankAccountId } =
    useBankOverviewSearchParams();
  const {
    filters: { companyUserId },
  } = useTransactionsFilters();

  const isAccessingCompanyUserTransactions = !!companyUserId;

  const { currency, currentBalance, nextBalance } = useMemo(() => {
    if (!selectedBankAccountId) {
      return {
        currency: DEFAULT_CURRENCY,
        currentBalance: getTotalBalance({
          bankAccounts,
          key: 'currentBalance',
        }),
        nextBalance: getTotalBalance({ bankAccounts, key: 'nextBalance' }),
      };
    }
    return (
      bankAccounts.find(
        ({ bankAccountId }) => bankAccountId === selectedBankAccountId,
      ) ?? PLACEHOLDER_BANK_ACCOUNT
    );
  }, [bankAccounts, selectedBankAccountId]);

  if (isAccessingCompanyUserTransactions) {
    return (
      <EmployeeTransactionsHeader
        companyUserId={companyUserId}
        currency={currency}
        loading={loading || !!error}
      />
    );
  }
  return (
    <AccountHeaderWithBalance
      currency={currency}
      currentBalance={currentBalance}
      loading={loading || !!error}
      nextBalance={nextBalance}
    />
  );
};

export default AccountHeader;
